// src/components/superadmin/SuperAdminCaseList.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BiSearch, BiTrash } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";

const SuperAdminCaseList = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const pageSize = 15;

  const fetchCases = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/super-admin/cases`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, pageSize },
      });
      setCases(response.data.data);
      setTotalPages(response.data.metadata.totalPages);
      setCurrentPage(response.data.metadata.currentPage);
    } catch (err) {
      console.error("Error fetching all cases:", err);
      setError("Failed to load cases. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedIds([]);
    fetchCases(currentPage);
  }, [currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (cases.length > 0 && selectedIds.length === cases.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(cases.map((c) => c.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected case(s)?`
      )
    )
      return;

    setIsDeleting(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${API_BASE_URL}/cases/bulk-delete`,
        { caseIds: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedIds([]);
      fetchCases(currentPage);
    } catch (err) {
      console.error("Error bulk deleting cases:", err);
      try {
        await Promise.all(
          selectedIds.map((id) =>
            axios.delete(`${API_BASE_URL}/cases/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          )
        );
        setSelectedIds([]);
        fetchCases(currentPage);
      } catch (fallbackErr) {
        setError(err.response?.data?.message || "Failed to delete selected cases.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete case "${name}"?`)) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API_BASE_URL}/cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedIds((prev) => prev.filter((item) => item !== id));
      fetchCases(currentPage);
    } catch (err) {
      console.error("Error deleting case:", err);
      setError(err.response?.data?.message || "Failed to delete case.");
    }
  };

  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleDateString() : "N/A";

  if (loading)
    return (
      <div className="p-6 text-center text-gray-500">Loading all cases...</div>
    );
  if (error) return <div className="p-6 text-center text-red-500">{error}</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center sm:text-left">
          All System Cases
        </h1>
      </div>

      {/* Bulk actions banner */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 transition-all">
          <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
            <span>{selectedIds.length} of {cases.length} selected</span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
            >
              Clear Selection
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition disabled:opacity-50"
          >
            <BiTrash className="mr-1 h-4 w-4" />
            {isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
          </button>
        </div>
      )}

      {/* Table Wrapper */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={cases.length > 0 && selectedIds.length === cases.length}
                    onChange={handleSelectAll}
                    aria-label="Select all cases"
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Case Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Case Number
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Organization
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Client
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {cases.length > 0 ? (
                cases.map((caseItem) => (
                  <tr
                    key={caseItem.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedIds.includes(caseItem.id) ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <td className="px-4 py-3 text-sm w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(caseItem.id)}
                        onChange={() => handleToggleSelect(caseItem.id)}
                        aria-label={`Select case ${caseItem.case_name}`}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <Link
                        to={`/super-admin/cases/${caseItem.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {caseItem.case_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {caseItem.case_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {caseItem.organization_name || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {caseItem.client_name || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {caseItem.status}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(caseItem.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium space-x-3">
                      <Link
                        to={`/super-admin/cases/${caseItem.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </Link>
                      <button
                        onClick={() => handleDelete(caseItem.id, caseItem.case_name)}
                        className="text-red-600 hover:text-red-800 inline-flex items-center"
                        title="Delete Case"
                      >
                        <BiTrash className="inline h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No cases found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>


        {/* Mobile Card Layout */}
        <div className="md:hidden space-y-4 p-2">
          {cases.length > 0 ? (
            cases.map((caseItem) => (
              <div
                key={caseItem.id}
                className={`border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition ${
                  selectedIds.includes(caseItem.id) ? "ring-2 ring-blue-500 bg-blue-50/30" : ""
                }`}
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(caseItem.id)}
                      onChange={() => handleToggleSelect(caseItem.id)}
                      aria-label={`Select case ${caseItem.case_name}`}
                      className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                    />
                    <h2 className="text-lg font-semibold text-gray-800">
                      {caseItem.case_name}
                    </h2>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Link
                      to={`/super-admin/cases/${caseItem.id}`}
                      className="text-blue-600 text-sm font-medium"
                    >
                      View
                    </Link>
                    <button
                      onClick={() => handleDelete(caseItem.id, caseItem.case_name)}
                      className="text-red-600 text-sm"
                      title="Delete Case"
                    >
                      <BiTrash className="inline h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Case No:</span>{" "}
                  {caseItem.case_number}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Organization:</span>{" "}
                  {caseItem.organization_name || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Client:</span>{" "}
                  {caseItem.client_name || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Status:</span>{" "}
                  {caseItem.status}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Created:</span>{" "}
                  {formatDate(caseItem.created_at)}
                </p>
              </div>
            ))
          ) : (
            <p className="text-center text-sm text-gray-500 py-4">
              No cases found.
            </p>
          )}
        </div>

      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center mt-6 gap-3">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition ${
              currentPage === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Previous
          </button>
          <span className="text-gray-700 text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition ${
              currentPage === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SuperAdminCaseList;
